import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, BellOff, X, AlertCircle } from 'lucide-react';
import { storage } from '../utils/storage';
import './NotificationPrompt.css';

/**
 * Componente para solicitar permiso de notificaciones push
 * Muestra un banner no intrusivo que el usuario puede aceptar o rechazar
 */
export function NotificationPrompt({
  isSupported,
  isSubscribed,
  permission,
  isLoading,
  error,
  onSubscribe,
  onDismiss,
}) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);
  const [showError, setShowError] = useState(false);

  // Verificar si ya se mostró el prompt
  useEffect(() => {
    const wasDismissed = storage.get('notification-prompt-dismissed');
    if (wasDismissed) {
      setDismissed(true);
    }
  }, []);

  // Mostrar error temporalmente
  useEffect(() => {
    if (error) {
      setShowError(true);
      const timer = setTimeout(() => setShowError(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleDismiss = () => {
    setDismissed(true);
    storage.set('notification-prompt-dismissed', 'true');
    onDismiss?.();
  };

  const handleSubscribe = async () => {
    const success = await onSubscribe();
    if (success) {
      setDismissed(true);
    }
  };

  // No mostrar si no está soportado, ya está subscrito, o fue rechazado
  if (!isSupported || isSubscribed || dismissed || permission === 'denied') {
    return null;
  }

  return (
    <div className="notification-prompt">
      {/* Error message */}
      {showError && (
        <div className="notification-prompt__error" role="alert">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      <div className="notification-prompt__content">
        <div className="notification-prompt__icon">
          <Bell />
        </div>

        <div className="notification-prompt__text">
          <h3 className="notification-prompt__title">
            {t('notifications.prompt.title', '¿Activar notificaciones?')}
          </h3>
          <p className="notification-prompt__description">
            {t(
              'notifications.prompt.description',
              'Recibe alertas de nuevos pedidos y actualizaciones importantes'
            )}
          </p>

          <div className="notification-prompt__buttons">
            <button
              type="button"
              onClick={handleSubscribe}
              disabled={isLoading}
              className="notification-prompt__btn-enable"
            >
              {isLoading
                ? t('notifications.prompt.enabling', 'Activando...')
                : t('notifications.prompt.enable', 'Activar')}
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              disabled={isLoading}
              className="notification-prompt__btn-later"
            >
              {t('notifications.prompt.later', 'Más tarde')}
            </button>
          </div>
        </div>

        <button type="button" onClick={handleDismiss} className="notification-prompt__close">
          <X />
        </button>
      </div>
    </div>
  );
}

/**
 * Toggle para activar/desactivar notificaciones en Settings
 */
export function NotificationToggle({
  isSupported,
  isSubscribed,
  permission,
  isLoading,
  error,
  onSubscribe,
  onUnsubscribe,
}) {
  const { t } = useTranslation();

  if (!isSupported) {
    return (
      <div className="notification-toggle notification-toggle--disabled">
        <div className="notification-toggle__content">
          <BellOff className="notification-toggle__icon" />
          <div>
            <p className="notification-toggle__title">
              {t('notifications.toggle.title', 'Notificaciones Push')}
            </p>
            <p className="notification-toggle__subtitle">
              {t('notifications.toggle.notSupported', 'No soportado en este navegador')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (permission === 'denied') {
    return (
      <div className="notification-toggle notification-toggle--blocked">
        <div className="notification-toggle__content">
          <BellOff className="notification-toggle__icon notification-toggle__icon--blocked" />
          <div>
            <p className="notification-toggle__title">
              {t('notifications.toggle.title', 'Notificaciones Push')}
            </p>
            <p className="notification-toggle__subtitle notification-toggle__subtitle--blocked">
              {t(
                'notifications.toggle.blocked',
                'Bloqueadas. Habilita en configuración del navegador'
              )}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handleToggle = async () => {
    if (isSubscribed) {
      await onUnsubscribe();
    } else {
      await onSubscribe();
    }
  };

  return (
    <div className="notification-toggle">
      <div className="notification-toggle__content">
        {isSubscribed ? (
          <Bell className="notification-toggle__icon notification-toggle__icon--active" />
        ) : (
          <BellOff className="notification-toggle__icon" />
        )}
        <div>
          <p className="notification-toggle__title">
            {t('notifications.toggle.title', 'Notificaciones Push')}
          </p>
          <p className="notification-toggle__subtitle">
            {isSubscribed
              ? t('notifications.toggle.enabled', 'Recibirás alertas de pedidos')
              : t('notifications.toggle.disabled', 'No recibirás alertas push')}
          </p>
          {error && (
            <p className="notification-toggle__error" role="alert">
              {error}
            </p>
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={handleToggle}
        disabled={isLoading}
        className={`notification-toggle__switch ${isSubscribed ? 'notification-toggle__switch--active' : ''} ${isLoading ? 'notification-toggle__switch--loading' : ''}`}
      >
        <span className="notification-toggle__switch-knob" />
      </button>
    </div>
  );
}

export default NotificationPrompt;
